/**
 * Generates a UUID v4. Uses `crypto.randomUUID()` when available (secure
 * contexts: HTTPS or localhost). Falls back to a `Math.random()`-based
 * implementation for non-secure contexts such as plain-HTTP local-network
 * access (e.g. http://192.168.x.x).
 */
export const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for non-secure contexts
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

/**
 * Calculates the percentage denominator for a compact scouting session.
 * Each tree consists of exactly 10 inspection points and the minimum is 10 trees
 * (100 points), ensuring backward compatibility with all historical records.
 */
export const getCompactDenominator = (
  records: Record<number, unknown>
): number => {
  const treeIndices = new Set(
    Object.keys(records).map(k => Math.floor(parseInt(k, 10) / 10))
  );
  return Math.max(10, treeIndices.size) * 10;
};

/**
 * Calculates pest percentages for a compact scouting session using a dynamic
 * denominator derived from the number of trees actually inspected.
 * The denominator is Math.max(10, numberOfTrees) * 10, so historical 10-tree
 * sessions continue to produce identical results to the previous hardcoded 100.
 */
export const calculateCompactPestPercentages = (
  tally: Record<string, number>,
  records: Record<number, unknown>
): Record<string, { count: number; percent: number; level?: string }> => {
  const denominator = getCompactDenominator(records);
  const result: Record<string, { count: number; percent: number; level?: string }> = {};
  for (const [pestId, count] of Object.entries(tally)) {
    result[pestId] = {
      count,
      percent: Math.round((count / denominator) * 100)
    };
  }
  return result;
};

// Display date/time formatting lives in ./dateFormat; re-exported here so the
// many existing `import { ... } from '../utils'` sites can reach the helpers.
export * from './dateFormat';

